import { Controller } from "@hotwired/stimulus";
import { get } from "@rails/request.js";
import escapeAttr from "./massimo/escape_attr";

// Picking a replication group fetches that POS's plans live and rebuilds the plan select's options.
// Stimulus counterpart of the React PosPlanExternalKeySelect, for shadcn-built pages.
//
// The group control is either the `group` select or a bare hidden input (`groupInput`) when there's only
// one eligible group. Groups in `hidePlanForGroupIds` (e.g. Nexus) hide and disable the plan field.
export default class extends Controller {
  static targets = ["group", "groupInput", "plan", "planField", "error"];
  static values = {
    plansUrlTemplate: String,
    hidePlanForGroupIds: { type: Array, default: [] },
    initialPlan: { type: String, default: "" },
  };

  connect() {
    this.applyGroup(this.currentGroupId(), this.initialPlanValue);
  }

  // Bound to the group select's change (the select controller dispatches it on its hidden input).
  groupChanged() {
    // The plan keys belong to the previously selected group, so clear them when the group changes.
    this.applyGroup(this.currentGroupId(), "");
  }

  async applyGroup(groupId, selectedPlan) {
    // Every group change, including clearing it, supersedes whatever fetch is still in flight.
    const token = (this.requestToken = Symbol("plans request"));
    const hidePlan = this.planHiddenFor(groupId);
    this.togglePlanField(!hidePlan);
    this.showError(null);

    if (!groupId || hidePlan) {
      this.populatePlans([], selectedPlan);
      return;
    }

    // Show the stored key while the fetch is in flight so a persisted plan never flashes blank.
    this.populatePlans(selectedPlan ? [{ pos_plan_external_key: selectedPlan, name: selectedPlan }] : [], selectedPlan);

    let data;
    try {
      const response = await get(this.plansUrlTemplateValue.replace("RG_ID", groupId), { responseKind: "json" });
      data = await response.json;
    } catch (error) {
      data = { error: error.message };
    }
    if (token !== this.requestToken) return;

    if (data.error) {
      this.showError(`Error fetching plans: ${data.error}`);
      return;
    }
    // Use whatever the operator has selected by now, not the value this fetch started with.
    this.populatePlans(data, this.currentPlan());
  }

  currentPlan() {
    return this.hasPlanTarget ? this.planTarget.querySelector('[data-select-target="hiddenInput"]')?.value ?? "" : "";
  }

  currentGroupId() {
    if (this.hasGroupInputTarget) return this.groupInputTarget.value;
    if (this.hasGroupTarget) return this.groupHiddenInput().value;
    return "";
  }

  groupHiddenInput() {
    return this.groupTarget.querySelector('[data-select-target="hiddenInput"]');
  }

  planHiddenFor(groupId) {
    return this.hidePlanForGroupIdsValue.map(String).includes(String(groupId));
  }

  togglePlanField(visible) {
    if (!this.hasPlanFieldTarget) return;
    this.planFieldTarget.hidden = !visible;
    const input = this.planTarget.querySelector('[data-select-target="hiddenInput"]');
    if (input) input.disabled = !visible;
  }

  showError(message) {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = message || "";
    this.errorTarget.hidden = !message;
  }

  // A stored plan the POS no longer lists stays selectable (mirrors the React picker's preserveMissingValue)
  // so saving an unrelated change never drops pos_plan_external_key. Uses the select controller's own
  // update methods rather than setSelectedValue, so this re-render fires no change event / dirties nothing.
  populatePlans(plans, selectedPlan) {
    if (!this.hasPlanTarget) return;

    const group = this.planTarget.querySelector('[role="group"]');
    const selected = String(selectedPlan || "");
    const known = plans.some((plan) => String(plan.pos_plan_external_key) === selected);
    const options = selected && !known ? [{ pos_plan_external_key: selected, name: selected }, ...plans] : plans;
    group.innerHTML = options.map((plan) => this.optionMarkup(plan, selected)).join("");

    const select = this.application.getControllerForElementAndIdentifier(this.planTarget, "select");
    if (!select) return;
    select.selectedValue = selected;
    select.updateCheckmarks();
    select.updateDisplay();
    select.updateHiddenInput();
  }

  // Mirrors Shadcn::SelectComponent#render_select_option.
  optionMarkup(plan, selectedValue) {
    const value = escapeAttr(String(plan.pos_plan_external_key ?? ""));
    const label = escapeAttr(String(plan.name ?? plan.pos_plan_external_key ?? ""));
    const check =
      value === escapeAttr(selectedValue)
        ? '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">' +
          '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>'
        : "";
    return (
      '<div data-ui--filter-target="item">' +
      '<div class="flex items-center space-x-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer ' +
      'hover:bg-accent hover:text-accent-foreground" data-select-target="option" ' +
      `data-select-value="${value}" data-action="click->select#selectOption">` +
      `<div class="w-4 h-4 shrink-0 text-brand-500">${check}</div>` +
      `<span class="flex-1 flex items-center gap-2">${label}</span>` +
      "</div></div>"
    );
  }
}
